import { logger } from '../logger';

class ChemistryNetwork {
  constructor() {
    this.learningRate = 0.01;
    this.initialized = false;
    this.initializationPromise = null;

    // Initialize with random weights (will be replaced when loaded from backend)
    this.weights1 = this.initializeWeights(4, 6);
    this.weights2 = this.initializeWeights(6, 1);
    this.bias1 = new Array(6).fill(0).map(() => Math.random() * 0.1);
    this.bias2 = [Math.random() * 0.1];
  }

  async initialize(apiClient) {
    // Avoid multiple initialization calls
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = (async () => {
      try {
        logger.debug('Loading chemistry weights from backend...');
        const response = await apiClient.getChemistryWeights();

        if (response && response.weights) {
          logger.debug('Loaded global chemistry weights from backend');
          this.weights1 = response.weights.weights1;
          this.weights2 = response.weights.weights2;
          this.bias1 = response.weights.bias1;
          this.bias2 = response.weights.bias2;
          this.initialized = true;
        } else {
          logger.warn('No chemistry weights found in backend. Run initialization script.');
          this.initialized = true;
        }
      } catch (error) {
        logger.error('Failed to load chemistry weights from backend:', error);
        this.initialized = true;
      }
    })();

    return this.initializationPromise;
  }

  initializeWeights(inputSize, outputSize) {
    return Array(inputSize)
      .fill(0)
      .map(() =>
        Array(outputSize)
          .fill(0)
          .map(() => (Math.random() - 0.5) * 2)
      );
  }

  sigmoid(x) {
    return 1 / (1 + Math.exp(-Math.max(-500, Math.min(500, x))));
  }

  relu(x) {
    return Math.max(0, x);
  }

  forward(inputs) {
    const hidden = this.bias1.map((bias, i) => {
      const sum =
        inputs.reduce((acc, input, j) => acc + input * this.weights1[j][i], 0) +
        bias;
      return this.relu(sum);
    });

    const output = this.bias2.map((bias, i) => {
      const sum =
        hidden.reduce((acc, h, j) => acc + h * this.weights2[j][i], 0) + bias;
      return this.sigmoid(sum);
    });

    return { hidden, output: output[0] };
  }

  predict(player1Stats, player2Stats) {
    const inputs = [
      player1Stats.mu / 50,
      player1Stats.sigma / 10,
      player2Stats.mu / 50,
      player2Stats.sigma / 10,
    ];

    return this.forward(inputs).output;
  }

  train(player1Stats, player2Stats, actualChemistry) {
    const inputs = [
      player1Stats.mu / 50,
      player1Stats.sigma / 10,
      player2Stats.mu / 50,
      player2Stats.sigma / 10,
    ];

    const { hidden, output } = this.forward(inputs);
    const error = actualChemistry - output;

    const outputGradient = error;

    for (let i = 0; i < this.weights2.length; i++) {
      this.weights2[i][0] += this.learningRate * outputGradient * hidden[i];
    }
    this.bias2[0] += this.learningRate * outputGradient;

    for (let i = 0; i < this.weights1.length; i++) {
      for (let j = 0; j < this.weights1[i].length; j++) {
        const hiddenError = outputGradient * this.weights2[j][0];
        if (hidden[j] > 0) {
          this.weights1[i][j] += this.learningRate * hiddenError * inputs[i];
        }
      }
    }
  }
}

export { ChemistryNetwork };

export const chemistryNetwork = new ChemistryNetwork();
